import {
  Badge,
  Flex,
  Icon,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaUserEdit } from 'react-icons/fa';
import { User } from 'services/@types';

interface LinkedUsersTableProps {
  users: User[];
}

export default function LinkedUsersTable({ users }: LinkedUsersTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const brandColor = useColorModeValue('brand.500', 'brand.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  return (
    <Card p="30px">
      <Flex justify="space-between" align="center" mb="20px">
        <Text color={textColor} fontSize="xl" fontWeight="700">
          {t('business_detail.linked_users') || 'Linked Users'} ({users.length})
        </Text>
      </Flex>
      {users.length === 0 ? (
        <Text color="gray.500" fontSize="sm">
          No users linked to this business
        </Text>
      ) : (
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th borderColor={borderColor}>Name</Th>
              <Th borderColor={borderColor}>Email</Th>
              <Th borderColor={borderColor}>Phone</Th>
              <Th borderColor={borderColor}>Roles</Th>
              <Th borderColor={borderColor}></Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((user) => (
              <Tr
                key={user.id}
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
                onClick={() =>
                  user.id &&
                  navigate(`/admin/brilliant_users/detail/${user.id}`)
                }
              >
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm" fontWeight="500">
                    {user.firstName || user.name || '-'}{' '}
                    {user.familyName || user.lastName || ''}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm">
                    {user.email}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm">
                    {user.phone || '-'}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Flex gap="4px" flexWrap="wrap">
                    {user.roles?.map((role) => (
                      <Badge
                        key={role}
                        colorScheme="brand"
                        fontSize="xs"
                        borderRadius="6px"
                      >
                        {role}
                      </Badge>
                    ))}
                  </Flex>
                </Td>
                <Td borderColor={borderColor}>
                  <Icon
                    as={FaUserEdit}
                    color={brandColor}
                    cursor="pointer"
                    onClick={() =>
                      navigate(
                        `/admin/brilliant_users/update_user/${user.id}`,
                        {
                          state: user,
                        },
                      )
                    }
                    transition="all 0.2s linear"
                    _hover={{ transform: 'scale(1.2)' }}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Card>
  );
}
